import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CreditCard, Gift, Link2, Package, RefreshCw, Store, Users } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

// 辣友卡 v2·年費會員——永久會員碼＋冷卻期自助核銷（每 30 日一盒、任選參與店鋪）
// 買年卡 HK$99 → 自動入會（即時可領首盒）→ 核銷 +30 日冷卻 → 可領前 3 日 WhatsApp 提醒
// 店鋪有每月配額＋存貨流水（wanla_restocks）——O2O 流量分發的數據證明

interface WanlaMember {
  id: number; phone: string; name: string; member_code: string | null;
  start_at: number; expire_at: number; next_push_at: number | null;
  last_reminded_at: number | null; renew_notified_at: number | null; send_at: number | null;
  status: string; claimed_count: number; last_claim_at: number | null;
}
interface WanlaClaim { code: string; phone: string; month: string; restaurant: string | null; shop_id: number | null; shop_name: string | null; claimed_at: number | null; created_at: number }
interface WanlaShop {
  id: number; name: string; address: string | null; note: string | null; is_active: number;
  monthly_quota: number; packs_in_stock: number;
  redeemed_month: number; redeemed_total: number; restocked_total: number;
  stock_remaining: number; status_label: string;
}
interface WanlaRestock { id: number; shop_id: number; shop_name: string | null; packs: number; note: string | null; created_at: number }

const AdminWanla: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<{ members: WanlaMember[]; claims: WanlaClaim[]; shops: WanlaShop[]; restocks: WanlaRestock[]; config: any } | null>(null);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({ month: '', restaurant: '', address: '', perk: '' });
  const [msg, setMsg] = useState('');
  const [newShop, setNewShop] = useState({ name: '', address: '', note: '', monthly_quota: 100, packs_in_stock: 0 });
  const [editShop, setEditShop] = useState<WanlaShop | null>(null);
  const [restockFor, setRestockFor] = useState<WanlaShop | null>(null);
  const [restockInput, setRestockInput] = useState({ packs: '', note: '' });
  const [copiedCode, setCopiedCode] = useState('');

  const fetchAll = async () => {
    setMsg('');
    const res = await apiFetch('/api/admin/wanla/overview');
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); return; }
      setMsg('載入失敗'); setLoading(false); return;
    }
    const d = (data as any).data || {};
    setData(d);
    if (d.config?.restaurant) setCfg({ month: d.config.month || '', restaurant: d.config.restaurant, address: d.config.address || '', perk: d.config.perk || '' });
    setLoading(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchAll();
  }, [navigate]);

  const saveCfg = async () => {
    const res = await apiFetch('/api/admin/wanla/config', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cfg) });
    const d = await res.json().catch(() => ({}));
    setMsg(res.ok ? '本月設定已儲存 ✓' : ((d as any).error || '儲存失敗'));
    fetchAll();
  };

  const pushDue = async () => {
    const res = await apiFetch('/api/admin/wanla/push-due', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg('推送處理失敗，稍後再試'); return; }
    const r = (d as any).data || {};
    setMsg(`已安排排程 ${r.scheduled || 0} 位（09:30-22:00 隨機時段錯峰）／提醒已發 ${r.sent || 0} 條／續會邀請 ${r.renewSent || 0} 條${r.failed ? `／失敗 ${r.failed}（自動重試）` : ''}`);
    fetchAll();
  };

  const addShop = async () => {
    if (!newShop.name.trim()) { setMsg('店鋪名必填'); return; }
    const res = await apiFetch('/api/admin/wanla/shops', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newShop) });
    if (res.ok) { setNewShop({ name: '', address: '', note: '', monthly_quota: 100, packs_in_stock: 0 }); setMsg('店鋪已新增 ✓'); fetchAll(); }
    else setMsg('新增失敗');
  };

  const saveShop = async (shop: Partial<WanlaShop>) => {
    const res = await apiFetch('/api/admin/wanla/shops', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(shop) });
    const d = await res.json().catch(() => ({}));
    if (res.ok) { setEditShop(null); setMsg('店鋪已儲存 ✓'); fetchAll(); }
    else setMsg((d as any).error || '儲存失敗');
  };

  const toggleShop = async (id: number) => {
    const res = await apiFetch('/api/admin/wanla/shops/toggle', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) });
    if (res.ok) fetchAll();
  };

  const doRestock = async () => {
    if (!restockFor) return;
    const packs = Number(restockInput.packs);
    if (!packs || packs <= 0) { setMsg('補貨盒數必須係正整數'); return; }
    const res = await apiFetch('/api/admin/wanla/restock', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ shop_id: restockFor.id, packs, note: restockInput.note }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg((d as any).error || '補貨失敗'); return; }
    const r = (d as any).data || {};
    setMsg(`已補貨 ${packs} 盒 → ${restockFor.name} 現餘 ${r.stock_remaining} 盒${r.needs_restock ? '（仍然偏低 ⚠️）' : ''}`);
    setRestockFor(null); setRestockInput({ packs: '', note: '' });
    fetchAll();
  };

  const copyMemberLink = async (code: string) => {
    await navigator.clipboard.writeText(`${window.location.origin}/wanla/${code}`);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const nowDay = Math.floor(Date.now() / 1000);
  const members = data?.members || [];
  const eligibleCount = members.filter((m) => m.status === 'active' && m.expire_at > nowDay && (m.next_push_at || 0) <= nowDay).length;
  const shops = data?.shops || [];
  const lowStockShops = shops.filter((sh) => sh.is_active && sh.stock_remaining <= 5);
  const monthClaims = (data?.claims || []).filter((c) => c.claimed_at && new Date(c.claimed_at * 1000).getMonth() === new Date().getMonth());
  const memberCount = members.length;

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm';
  const card = 'bg-white dark:bg-gray-800 rounded-xl shadow p-4';
  const smInput = 'px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm w-24';

  return (
    <AdminLayout currentPage="wanla">
      <div className="space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-red-600" /> 辣友卡·年費會員
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">年費 HK$99 · 買卡即時可領首盒 → 每 30 日一盒（任選參與店鋪）· 可領前 3 日 WhatsApp 提醒錯峰推送</p>
        </div>

        {msg && <div className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 p-3 rounded-lg text-sm">{msg}</div>}

        {/* 總覽 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className={card + ' text-center'}>
            <div className="text-2xl font-bold text-gray-800 dark:text-white">{loading ? '—' : memberCount}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">年費會員</div>
          </div>
          <div className={card + ' text-center'}>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{loading ? '—' : monthClaims.length}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">本月換領</div>
          </div>
          <div className={card + ' text-center'}>
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{loading ? '—' : eligibleCount}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">現可領取</div>
          </div>
          <div className={card + ' text-center'}>
            <div className={`text-2xl font-bold ${lowStockShops.length ? 'text-red-600' : 'text-gray-800 dark:text-white'}`}>{loading ? '—' : lowStockShops.length}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">需補貨店鋪</div>
          </div>
        </div>

        {/* 本月換領設定 */}
        <div className={card + ' space-y-3'}>
          <h3 className="font-bold text-gray-800 dark:text-white">📅 本月換領設定（提醒訊息引用嘅推薦店）</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-gray-500 dark:text-gray-400">月份</label><input value={cfg.month} onChange={(e) => setCfg({ ...cfg, month: e.target.value })} placeholder="2026-09" className={inputCls} /></div>
            <div><label className="text-xs text-gray-500 dark:text-gray-400">本月推薦餐廳 *</label><select value={cfg.restaurant} onChange={(e) => { const shop = shops.find((x) => x.name === e.target.value); setCfg({ ...cfg, restaurant: e.target.value, address: shop?.address || cfg.address }); }} className={inputCls}>
              {shops.filter((x) => x.is_active).map((x) => <option key={x.id} value={x.name}>{x.name}</option>)}
              {cfg.restaurant && !shops.some((x) => x.name === cfg.restaurant) && <option value={cfg.restaurant}>{cfg.restaurant}（現行）</option>}
            </select></div>
            <div className="sm:col-span-2"><label className="text-xs text-gray-500 dark:text-gray-400">餐廳地址</label><input value={cfg.address} onChange={(e) => setCfg({ ...cfg, address: e.target.value })} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className="text-xs text-gray-500 dark:text-gray-400">本月聖品</label><input value={cfg.perk} onChange={(e) => setCfg({ ...cfg, perk: e.target.value })} className={inputCls} /></div>
          </div>
          <button onClick={saveCfg} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm font-medium">儲存本月設定</button>
        </div>

        {/* 提醒推送 */}
        <div className={card + ' flex items-center justify-between flex-wrap gap-2'}>
          <div className="text-sm text-gray-600 dark:text-gray-300 max-w-3xl">會員可領日前 <b>3 日</b>自動 WhatsApp 提醒（當日 09:30-22:00 隨機時段錯峰防封；同一週期只提醒一次）；會籍到期前 <b>30 日</b>自動續會邀請。每日 09:00 系統自動處理，亦可手動觸發。</div>
          <button onClick={pushDue} className="px-4 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 text-sm font-medium whitespace-nowrap">立即處理到期推送</button>
        </div>

        {/* 參與店鋪 */}
        <div className={card}>
          <h3 className="font-bold text-gray-800 dark:text-white text-sm">🏪 參與店鋪（每月配額＋存貨流水——換領數據就係賣俾店鋪嘅流量證明）</h3>
          {lowStockShops.length > 0 && (
            <div className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium flex items-center gap-1">
              <AlertTriangle size={15} /> {lowStockShops.map((s) => s.name).join('、')}——存貨 ≤ 5 盒，需補貨
            </div>
          )}
          <div className="mt-3 space-y-2">
            {shops.map((sh) => (
              <div key={sh.id} className="border-b border-gray-100 dark:border-gray-700 pb-2">
                <div className="flex items-center justify-between gap-3 text-sm flex-wrap">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-800 dark:text-white">
                      {sh.name} {!sh.is_active && <span className="text-gray-400 text-xs">(停用)</span>}
                      {sh.is_active === 1 && sh.status_label !== 'ok' && <span className="ml-1 text-xs text-red-500">（{sh.status_label === 'full' ? '本月已滿' : '存貨不足'}）</span>}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{sh.address}{sh.note ? ` · ${sh.note}` : ''}</div>
                  </div>
                  <div className="text-right text-xs whitespace-nowrap">
                    <div className="text-gray-600 dark:text-gray-300">本月 <b className={sh.redeemed_month >= sh.monthly_quota ? 'text-red-500' : 'text-green-600 dark:text-green-400'}>{sh.redeemed_month}</b>/{sh.monthly_quota} · 累計 {sh.redeemed_total}</div>
                    <div className={sh.stock_remaining <= 5 ? 'text-red-600 font-bold' : 'text-gray-500'}>{sh.stock_remaining <= 5 ? `⚠️ 需補貨（餘 ${sh.stock_remaining}）` : `存貨 ${sh.stock_remaining} 盒`}</div>
                  </div>
                  <div className="flex gap-1.5 whitespace-nowrap">
                    <button onClick={() => { setRestockFor(sh === restockFor ? null : sh); setEditShop(null); setRestockInput({ packs: '', note: '' }); }} className="text-xs px-2 py-1 rounded bg-orange-500 text-white hover:bg-orange-600"><Package size={12} className="inline mr-0.5" />補貨</button>
                    <button onClick={() => { setEditShop(sh === editShop ? null : sh); setRestockFor(null); }} className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300">編輯</button>
                    <button onClick={() => toggleShop(sh.id)} className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300">{sh.is_active ? '停用' : '啟用'}</button>
                  </div>
                </div>
                {restockFor?.id === sh.id && (
                  <div className="mt-2 grid sm:grid-cols-3 gap-2 items-end bg-orange-50 dark:bg-orange-900/20 p-2 rounded-lg">
                    <div><label className="text-xs text-gray-500 dark:text-gray-400">補貨盒數 *</label><input type="number" min={1} value={restockInput.packs} onChange={(e) => setRestockInput({ ...restockInput, packs: e.target.value })} placeholder="例如 30" className={inputCls} /></div>
                    <div><label className="text-xs text-gray-500 dark:text-gray-400">備註</label><input value={restockInput.note} onChange={(e) => setRestockInput({ ...restockInput, note: e.target.value })} placeholder="供應商／批次等" className={inputCls} /></div>
                    <button onClick={doRestock} className="px-4 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 text-sm font-medium">確認補貨（現餘 {sh.stock_remaining}）</button>
                  </div>
                )}
                {editShop?.id === sh.id && (
                  <div className="mt-2 grid sm:grid-cols-4 gap-2 items-end bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg">
                    <div className="sm:col-span-2"><label className="text-xs text-gray-500 dark:text-gray-400">名稱</label><input value={editShop.name} onChange={(e) => setEditShop({ ...editShop, name: e.target.value })} className={inputCls} /></div>
                    <div><label className="text-xs text-gray-500 dark:text-gray-400">每月配額</label><input type="number" min={0} value={editShop.monthly_quota} onChange={(e) => setEditShop({ ...editShop, monthly_quota: Number(e.target.value) })} className={smInput} /></div>
                    <div><label className="text-xs text-gray-500 dark:text-gray-400">存貨基線</label><input type="number" min={0} value={editShop.packs_in_stock} onChange={(e) => setEditShop({ ...editShop, packs_in_stock: Number(e.target.value) })} className={smInput} /></div>
                    <div className="sm:col-span-3"><label className="text-xs text-gray-500 dark:text-gray-400">地址</label><input value={editShop.address || ''} onChange={(e) => setEditShop({ ...editShop, address: e.target.value })} className={inputCls} /></div>
                    <button onClick={() => saveShop(editShop)} className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-800 text-sm font-medium">儲存</button>
                    <div className="sm:col-span-4 text-xs text-gray-400">存貨基線＝開档初始數；之後補貨走流水帳——餘量 = 基線＋補貨總數（{sh.restocked_total}）－核銷（{sh.redeemed_total}）</div>
                  </div>
                )}
              </div>
            ))}
            {shops.length === 0 && <div className="text-center py-6 text-gray-400 text-sm">仲未有參與店鋪</div>}
          </div>
          <div className="mt-3 grid sm:grid-cols-5 gap-2 items-end">
            <input value={newShop.name} onChange={(e) => setNewShop({ ...newShop, name: e.target.value })} placeholder="新店鋪名 *" className={inputCls} />
            <input value={newShop.address} onChange={(e) => setNewShop({ ...newShop, address: e.target.value })} placeholder="地址" className={inputCls} />
            <input type="number" min={0} value={newShop.monthly_quota} onChange={(e) => setNewShop({ ...newShop, monthly_quota: Number(e.target.value) })} placeholder="每月配額" className={inputCls} />
            <input type="number" min={0} value={newShop.packs_in_stock} onChange={(e) => setNewShop({ ...newShop, packs_in_stock: Number(e.target.value) })} placeholder="存貨基線" className={inputCls} />
            <button onClick={addShop} className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-800 text-sm font-medium whitespace-nowrap"><Store size={14} className="inline mr-1" />新增店鋪</button>
          </div>
        </div>

        {/* 會員列表 */}
        <div className={card}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-bold text-gray-800 dark:text-white text-sm flex items-center gap-1"><Users size={14} /> 會員列表（會員碼＝終身換領憑證）</h3>
            <button onClick={fetchAll} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 flex items-center gap-1"><RefreshCw size={12} /> 重新整理</button>
          </div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 dark:bg-gray-700 text-left">
                <tr><th className="px-3 py-2">會員</th><th className="px-3 py-2">電話</th><th className="px-3 py-2">會員碼</th><th className="px-3 py-2">到期</th><th className="px-3 py-2 text-center">已換領</th><th className="px-3 py-2">下次可領</th><th className="px-3 py-2">上次提醒</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {members.map((m) => {
                  const eligible = m.status === 'active' && m.expire_at > nowDay && (m.next_push_at || 0) <= nowDay;
                  return (
                    <tr key={m.id} className="hover:bg-stone-50 dark:hover:bg-gray-700/50">
                      <td className="px-3 py-2 text-gray-800 dark:text-white">{m.name || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{m.phone}</td>
                      <td className="px-3 py-2">
                        {m.member_code ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="font-mono font-bold">{m.member_code}</span>
                            <button onClick={() => copyMemberLink(m.member_code!)} className="text-xs text-blue-500 hover:text-blue-700" title="複製換領頁連結">
                              {copiedCode === m.member_code ? '✓ 已複製' : <Link2 size={13} className="inline" />}
                            </button>
                          </span>
                        ) : <span className="text-xs text-gray-400">待生成</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">{new Date(m.expire_at * 1000).toLocaleDateString('zh-HK')}</td>
                      <td className="px-3 py-2 text-center">{m.claimed_count}/12</td>
                      <td className="px-3 py-2 text-xs">
                        {eligible ? <span className="text-green-600 font-medium">✓ 現在可領</span>
                          : m.next_push_at ? new Date(m.next_push_at * 1000).toLocaleDateString('zh-HK') : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">{m.last_reminded_at ? new Date(m.last_reminded_at * 1000).toLocaleDateString('zh-HK') : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {members.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">仲未有辣友卡會員——客人喺商店購買「辣友卡·年費會員」並完成付款後自動入會</div>}
          </div>
        </div>

        {/* 換領記錄 */}
        <div className={card}>
          <h3 className="font-bold text-gray-800 dark:text-white text-sm mb-2 flex items-center gap-1"><Gift size={14} /> 換領記錄（最近）</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 dark:bg-gray-700 text-left">
                <tr><th className="px-3 py-2">收據碼</th><th className="px-3 py-2">電話</th><th className="px-3 py-2">店鋪</th><th className="px-3 py-2">月份</th><th className="px-3 py-2">狀態</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {(data?.claims || []).slice(0, 20).map((c, ci) => (
                  <tr key={ci} className="hover:bg-stone-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 py-2 font-mono text-xs">{c.code}</td>
                    <td className="px-3 py-2 font-mono text-xs">{c.phone}</td>
                    <td className="px-3 py-2 text-xs">{c.shop_name || c.restaurant || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{c.month}</td>
                    <td className="px-3 py-2 text-xs">
                      {c.claimed_at
                        ? <span className="text-green-600">✓ {new Date(c.claimed_at * 1000).toLocaleDateString('zh-HK')}</span>
                        : <span className="text-gray-400">舊制未核銷</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(data?.claims?.length || 0) === 0 && <div className="text-center text-gray-400 py-4 text-sm">暫無換領記錄</div>}
          </div>
        </div>

        {/* 補貨記錄 */}
        <div className={card}>
          <h3 className="font-bold text-gray-800 dark:text-white text-sm mb-2 flex items-center gap-1"><Package size={14} /> 補貨記錄（最近）</h3>
          <div className="space-y-1">
            {(data?.restocks || []).slice(0, 10).map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700 py-1.5">
                <span>{r.shop_name || `店鋪 #${r.shop_id}`}</span>
                <span className="text-orange-600 font-medium">+{r.packs} 盒</span>
                <span className="text-gray-400 truncate max-w-[40%]">{r.note}</span>
                <span className="text-gray-500">{new Date(r.created_at * 1000).toLocaleString('zh-HK', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
            {(data?.restocks?.length || 0) === 0 && <div className="text-center text-gray-400 py-4 text-sm">暫無補貨記錄</div>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminWanla;
